import React from 'react'
import { Box, Button, Skeleton, TableCell, TableRow, Typography } from '@mui/material'
import { color, focusRing, radius, shadow, type } from './tokens'

/** White surface every section sits on. */
export function Card({ children, sx, padded = true, ...rest }) {
  return (
    <Box
      sx={{
        bgcolor: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius.card,
        boxShadow: shadow.card,
        p: padded ? { xs: 2.5, md: 3.5 } : 0,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  )
}

/** Title + one line of context, with an optional action on the right. */
export function CardHeader({ title, subtitle, action, sx }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5, ...sx }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h2" sx={type.sectionTitle}>{title}</Typography>
        {subtitle && <Typography sx={{ ...type.small, mt: 0.5 }}>{subtitle}</Typography>}
      </Box>
      {action}
    </Box>
  )
}

export function PrimaryButton({ children, sx, ...rest }) {
  return (
    <Button
      disableElevation
      variant="contained"
      sx={{
        bgcolor: color.accent,
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 15,
        borderRadius: radius.control,
        px: 2.5,
        py: 1.1,
        boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
        '&:hover': { bgcolor: color.accentHover, boxShadow: '0 2px 5px rgba(99,91,255,0.3)' },
        '&.Mui-disabled': { bgcolor: color.accent, color: '#fff', opacity: 0.6 },
        ...focusRing,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Button>
  )
}

const PILL = {
  success: { bg: color.successSoft, fg: color.success },
  warning: { bg: color.warningSoft, fg: color.warningText },
  error: { bg: color.errorSoft, fg: color.error },
  neutral: { bg: '#EBEEF1', fg: color.muted },
  accent: { bg: color.accentSoft, fg: color.accent },
}

/** Small status badge: Paid, Pending, Refunded… */
export function StatusPill({ tone = 'neutral', children }) {
  const t = PILL[tone] || PILL.neutral
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: '2px',
        borderRadius: radius.control,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: '18px',
        whiteSpace: 'nowrap',
        bgcolor: t.bg,
        color: t.fg,
      }}
    >
      {children}
    </Box>
  )
}

/** An icon in a soft circle, a headline and one sentence — never a blank table. */
export function EmptyState({ icon: Icon, title, body }) {
  return (
    <Box sx={{ py: { xs: 5, md: 7 }, px: 2, textAlign: 'center' }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          bgcolor: color.accentSoft,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {Icon && <Icon sx={{ color: color.accent, fontSize: 26 }} />}
      </Box>
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: color.text }}>{title}</Typography>
      {body && <Typography sx={{ ...type.small, mt: 0.75, maxWidth: 360, mx: 'auto' }}>{body}</Typography>}
    </Box>
  )
}

/** Placeholder rows shaped like the table they stand in for. */
export function SkeletonRows({ rows = 5, columns = 6 }) {
  return Array.from({ length: rows }).map((_, r) => (
    <TableRow key={r}>
      {Array.from({ length: columns }).map((__, c) => (
        <TableCell key={c}>
          <Skeleton variant="text" width={c === 0 ? 24 : `${55 + ((r + c) % 3) * 15}%`} sx={{ bgcolor: '#EEF1F5' }} />
        </TableCell>
      ))}
    </TableRow>
  ))
}

/** Table styling shared by every list on the page. */
export const tableSx = {
  '& th': {
    fontSize: 12,
    fontWeight: 600,
    color: color.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: `1px solid ${color.border}`,
    bgcolor: color.surface,
    whiteSpace: 'nowrap',
  },
  '& td': {
    fontSize: 14,
    color: color.text,
    borderBottom: `1px solid ${color.divider}`,
  },
}
